export type KpiSectionOrderable = {
  id: string;
  updatedAt?: string | Date;
  config?: {
    sortOrder?: unknown;
    [key: string]: unknown;
  } | null;
};

const toSortOrder = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const toUpdatedAtMs = (value: string | Date | undefined): number => {
  if (!value) return 0;
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(ms) ? ms : 0;
};

/**
 * Ordina le sezioni KPI per `config.sortOrder` (persistito nel JSON, senza migrazione).
 * Fallback: `updatedAt` DESC come fa l'API. Se `overrideIds` è valorizzato (drag ottimistico),
 * ha priorità sulle sezioni ancora presenti.
 */
export const orderKpiSections = <T extends KpiSectionOrderable>(
  sections: T[],
  overrideIds?: string[] | null,
): T[] => {
  if (sections.length === 0) return [];

  const sectionById = new Map(sections.map((section) => [section.id, section]));

  if (overrideIds && overrideIds.length > 0) {
    const orderedExisting = overrideIds
      .map((id) => sectionById.get(id))
      .filter((section): section is T => Boolean(section));
    const orderedIds = new Set(orderedExisting.map((section) => section.id));
    const missing = sections.filter((section) => !orderedIds.has(section.id));
    return [...orderedExisting, ...orderKpiSections(missing)];
  }

  return [...sections].sort((a, b) => {
    const aOrder = toSortOrder(a.config?.sortOrder);
    const bOrder = toSortOrder(b.config?.sortOrder);

    if (aOrder !== null && bOrder !== null && aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    if (aOrder !== null && bOrder === null) return -1;
    if (aOrder === null && bOrder !== null) return 1;

    return toUpdatedAtMs(b.updatedAt) - toUpdatedAtMs(a.updatedAt);
  });
};

/** Prossimo sortOrder per una sezione nuova (in coda). */
export const nextKpiSectionSortOrder = (
  sections: KpiSectionOrderable[],
): number => {
  let max = -1;
  for (const section of sections) {
    const order = toSortOrder(section.config?.sortOrder);
    if (order !== null && order > max) max = order;
  }
  return max + 1;
};
